<template>
  <div class="dean-form-row">
    <div class="row">
      <div :class="inputDisplayType === 'row' ? 'small-6 medium-6 large-6 hd-6 columns' : 'small-12 medium-12 large-12 hd-12 columns'" v-if="label">
        <p class="input-label mb-0">
          {{ label }}
          <span
              v-if="requiredField"
              class="required-field">
            *
          </span>
        </p>
      </div>
      <div :class="label || inputDisplayType === 'row' ? 'small-6 medium-6 large-6 hd-6 columns' : 'small-12 medium-12 large-12 hd-12 columns'">
        <div class="date-input">
          <b-input-group>
            <b-form-input
                id="datepicker-input"
                v-model="valueFormatted"
                type="text"
                pattern="\d{2}/\d{2}/\d{4}"
                minlength="10"
                maxlength="10"
                placeholder="DD/MM/YYYY"
                autocomplete="off"
                :state="isValid()"
                :disabled="disabled"
            ></b-form-input>
            <b-input-group-append>
              <b-form-datepicker
                id="datepicker-calendar"
                selected-variant="primary"
                :button-only="true"
                boundary="scrollParent"
                v-model="date"
                start-weekday="1"
                locale="en-GB"
                :placeholder="getPlaceholder"
                :min="min"
                :max="max"
                :date-disabled-fn="dateDisabled"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                aria-controls="datepicker-input"
                :disabled="disabled"
              >
              </b-form-datepicker>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { BFormDatepicker, BInputGroup, BInputGroupAppend, BFormInput } from 'bootstrap-vue'
import moment from 'moment';
import axios from 'axios';
import { Version } from '@/plugins/ovVersion';

@Component({
  components: {
    BFormDatepicker,
    BInputGroup,
    BInputGroupAppend,
    BFormInput,
  },
})

export default class DateInput extends Vue {
  @Prop() public label!: string;
  @Prop() public value!: string|null;
  @Prop() public requiredField!: boolean;
  @Prop({default: false}) disabled?: boolean;
  @Prop({default: false}) public warning!: boolean;
  @Prop() public productId!: null|string;
  @Prop() public placeholder!: string;
  @Prop() public minDate!: Date|null;
  @Prop() public maxDate!: Date|null;
  @Prop({default: true}) public disableWeekends!: boolean;
  @Prop({default: true}) public disableHolidays!: boolean;
  @Prop({default: 'row'}) public inputDisplayType!: string;

  private holidays: string[] = [];
  private min: null | Date = null;
  private max: null | Date = null;

  public date: null | Date | string = null;
  public valueFormatted: string = '';

  get getPlaceholder(): string {
    if (this.requiredField) {
      return this.placeholder + ' *';
    }

    return this.placeholder;
  }

  @Watch('value')
  public currentDate(): void {
    if (this.value) {
      this.date = new Date(this.value);
      this.valueFormatted = this.date.toLocaleDateString('en-GB');
    } else {
      this.date = null;
      this.valueFormatted = '';
    }
  }

  @Watch('valueFormatted')
  public valueFormattedChanged(): void {
    if (this.valueFormatted == null || this.valueFormatted == '') {
      this.date = null;

      return;
    }

    const date = moment(this.valueFormatted, 'DD/MM/YYYY', true);

    if (date.isValid()) {
      this.date = new Date(date);
    }
  }

  @Watch('warning')
  private isValid(): boolean {
    return !this.warning;
  }

  @Watch('date')
  private dateValueChanged(): void {
    let date = this.date;

    if (date) {
      const isValidDate = moment(this.date);

      if (isValidDate.isValid()) {
        date = moment(this.date).format('YYYY/MM/DD');
      }

    }

    this.$emit('dateValueChanged', date);
  }

  @Watch('minDate')
  private changeMinDate(): void {
    if (this.minDate) {
      this.min = new Date(this.minDate);
    }
  }

  @Watch('maxDate')
  private changeMaxDate(): void {
    if (this.maxDate) {
      this.max = new Date(this.maxDate);
    }
  }

  private dateDisabled(ymd: string, date: Date): boolean {
    let disableWeekend = false;

    if (this.disableWeekends) {
      disableWeekend = date.getDay() === 0 || date.getDay() === 6;
    }

    let disableHolidays = false;

    if (this.disableHolidays) {
      disableHolidays = this.holidays.includes(ymd);
    }

    return disableWeekend || disableHolidays;
  }

  public async created() {
    interface Holiday {
      dates: string[];
      expiry: string;
    }

    let holidays: Holiday|null = JSON.parse(localStorage.getItem('holidays')!);

    let version: string|null = localStorage.getItem('app_version');

    // reset local storage if version is not stored
    // or if version from storage does not match to the latest version
    if (null === version || version !== Version()) {
      holidays = null;
      localStorage.removeItem('app_version');
      localStorage.setItem('app_version', Version());
    }

    if (
        null === holidays
        ||
        moment(holidays.expiry).format('YYYY-MM-DD') === moment(new Date()).format('YYYY-MM-DD')
    ) {
      await axios
          .get('/-/trade/api/holidays.list')
          .then(
              (response: any) => {
                holidays = {
                  dates: response.data,
                  expiry: moment(new Date(new Date().getFullYear() + 1, 0, 1)).format('YYYY-MM-DD'),
                };
              },
              (error: any) => {
                // tslint:disable-next-line:no-console
                console.error(error);
              },
          );

      localStorage.setItem('holidays', JSON.stringify(holidays));
    }

    if (holidays) {
      this.holidays = holidays.dates;
    }

    if (this.value) {
      if (this.value === 'as_soon_as_possible_date') {
        this.date = this.value;
      } else {
        this.date = new Date(this.value);
      }
    }

    this.changeMinDate();
    this.changeMaxDate();
  }
}
</script>

<!-- STYLE -->
<style lang="scss" scoped>

// IMPORT
@import '~@deanone/dean-components/src/scss/vars';

.required-field {
  color: #D70019;
}

.input-label {
  padding-left: 20px;
}

.date-input {
  justify-content: flex-end;
  align-content: center;
  padding: 5px 10px 5px 10px;
  width: 100%; // if wish date type set 50%

  .form-control {
    border-color: var(--color-light-shade-1, #ededf2) !important;
    border-top-left-radius: 10px !important;
    border-bottom-left-radius: 10px !important;
    height: 40px !important;
    background-image: none !important;

    &.is-invalid {
      border-color: var(--color-info-warning, $color-info-warning) !important;
      background-image: none !important;
    }
  }
}

</style>